// Settings → Users panel. Lists every user account in the system,
// lets a users:manage holder disable/enable inline, and lets a
// users:create holder mint new accounts with the one-time-password
// reveal pattern.
//
// 0.1.7+ added the underlying POST /users (users:create) and
// PATCH /users/:id (users:manage) endpoints — this panel just
// surfaces them. Server-side cap checks are authoritative; if the
// caller lacks the cap, the action returns 403 and the toast
// surfaces it.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { apiFetch, apiList, apiSingle, ApiError } from '../lib/api.js';
import { toast } from './Toast.js';
import { copyToClipboard } from '../lib/clipboard.js';
import { UserPlus, UserCheck, UserX, Copy, KeyRound, Building2, ChevronDown, ChevronRight } from 'lucide-react';

interface UserRow {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  isActive: boolean;
  createdAt: string;
  groupIds: string[];
  roleIds: string[];
}

// CTSK-239 Phase 2 (0.1.245) — one row per org this user belongs to,
// with the per-org role that now actually grants their capabilities
// there (folded into loadCapabilities as of this ship). Backed by
// GET /users/:id/memberships.
interface UserMembershipRow {
  orgId: string;
  orgName: string;
  orgSlug: string;
  roleId: string;
  roleName: string;
  isOwner: boolean;
  joinedAt: string;
}

interface CreateUserResponse extends UserRow {
  tempPassword?: string;
}

export function UsersPanel() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Create-user form state
  const [createOpen, setCreateOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');

  // One-time temp-password reveal banner — see CreateUser flow in
  // OrganizationsPanel for the same pattern.
  const [revealedPassword, setRevealedPassword] = useState<{
    email: string;
    password: string;
  } | null>(null);

  const usersQ = useQuery({
    queryKey: ['users', { search }],
    queryFn: () => apiList<UserRow>('/users', {
      query: { q: search.trim() || undefined },
    }),
  });

  // CTSK-239 Phase 2 — cross-org role table, expanded per-row on demand
  // rather than fetched for every user up front (avoids an N+1 fan-out
  // against /users/:id/memberships on every panel load).
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const membershipsQ = useQuery({
    queryKey: ['user-memberships', expandedUserId],
    queryFn: () => apiList<UserMembershipRow>(`/users/${expandedUserId}/memberships`),
    enabled: !!expandedUserId,
  });

  const createMut = useMutation({
    mutationFn: () =>
      apiSingle<CreateUserResponse>('/users', {
        method: 'POST',
        body: {
          email: newEmail.trim().toLowerCase(),
          displayName: newDisplayName.trim() || undefined,
        },
      }),
    onSuccess: (data) => {
      if (data.tempPassword) {
        setRevealedPassword({ email: data.email, password: data.tempPassword });
      }
      toast.success(`User created: ${data.email}`);
      setCreateOpen(false);
      setNewEmail('');
      setNewDisplayName('');
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to create user'),
  });

  // Admin-triggered password reset — CTSK-238. Same per-row action
  // shape as toggleActiveMut; the response's one-time tempPassword
  // feeds the same reveal banner the create flow uses.
  const resetPasswordMut = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiSingle<{ id: string; email: string; tempPassword: string }>(
        `/users/${id}/reset-password`,
        { method: 'POST' },
      ),
    onSuccess: (data) => {
      setRevealedPassword({ email: data.email, password: data.tempPassword });
      toast.success(`Password reset for ${data.email}`);
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to reset password'),
  });

  const toggleActiveMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiSingle<UserRow>(`/users/${id}`, {
        method: 'PATCH',
        body: { isActive },
      }),
    onSuccess: (_d, { isActive }) => {
      toast.success(isActive ? 'User enabled' : 'User disabled');
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to update user'),
  });

  if (usersQ.isError) {
    const err = usersQ.error;
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 403) {
      return (
        <div style={{ padding: 20, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13 }}>
          Listing users requires the <code>users:view</code> capability.
        </div>
      );
    }
    return <div style={{ color: 'var(--danger-primary, #B3261E)' }}>Failed to load users.</div>;
  }

  const allUsers = usersQ.data?.data ?? [];
  const users = showInactive ? allUsers : allUsers.filter((u) => u.isActive);
  const activeCount = allUsers.filter((u) => u.isActive).length;
  const inactiveCount = allUsers.length - activeCount;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* One-time password reveal — identical pattern to the same
          banner in OrganizationsPanel. Captures and lets the operator
          copy the temp password the API only returns once. */}
      {revealedPassword && (
        <section
          role="alert"
          style={{
            background: 'color-mix(in srgb, var(--warning-primary) 12%, var(--surface-raised))',
            border: '1px solid var(--warning-primary)',
            borderRadius: 'var(--radius-md)',
            padding: 16,
            color: 'var(--text-primary)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <strong style={{ fontSize: 14, color: 'var(--warning-primary)' }}>
              One-time password for {revealedPassword.email}
            </strong>
            <button
              type="button"
              onClick={() => setRevealedPassword(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Dismiss
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <code
              style={{
                flex: 1,
                fontFamily: 'var(--font-mono)',
                fontSize: 14,
                padding: '8px 12px',
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-strong)',
                borderRadius: 4,
                userSelect: 'all',
              }}
            >
              {revealedPassword.password}
            </code>
            <button
              type="button"
              onClick={() => {
                // CTSK-172: use copyToClipboard helper. Raw
                // navigator.clipboard.writeText fails silently on http://
                // origins (Vault UI runs on http://192.168.153.164:5173/,
                // not a secure context) — copyToClipboard has a document.execCommand
                // fallback for the insecure-context case. Was noted as broken
                // during CTSK-238 smoke 2026-07-30.
                void copyToClipboard(revealedPassword.password).then((ok) => {
                  if (ok) toast.success('Password copied');
                  else toast.error('Clipboard write failed');
                });
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '8px 12px',
                fontSize: 12,
                border: '1px solid var(--border-strong)',
                borderRadius: 4,
                background: 'var(--surface-raised)',
                cursor: 'pointer',
              }}
            >
              <Copy size={12} /> Copy
            </button>
          </div>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>
            This is the only time the password will be shown.
          </p>
        </section>
      )}

      <section style={{ background: 'var(--surface-raised)', padding: 20, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>Users</h2>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show disabled
            </label>
            {!createOpen && (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 10px',
                  fontSize: 12,
                  border: '1px solid var(--border-strong)',
                  borderRadius: 6,
                  background: 'var(--surface-raised)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                }}
              >
                <UserPlus size={12} /> New user
              </button>
            )}
          </div>
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 0 }}>
          {activeCount} active · {inactiveCount} disabled · {allUsers.length} total
        </p>

        {/* Inline create form. Same shape as the one in
            OrganizationsPanel — duplicated rather than extracted so
            each surface can iterate independently. */}
        {createOpen && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!newEmail.trim()) return;
              createMut.mutate();
            }}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              alignItems: 'center',
              padding: 12,
              background: 'var(--surface-sunken)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 12,
            }}
          >
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="user@example.com"
              required
              autoFocus
              style={{
                flex: '1 1 220px',
                minWidth: 180,
                padding: '6px 10px',
                fontSize: 13,
                border: '1px solid var(--border-strong)',
                borderRadius: 4,
                fontFamily: 'var(--font-mono)',
              }}
            />
            <input
              type="text"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              placeholder="Display name (optional)"
              style={{
                flex: '1 1 180px',
                minWidth: 140,
                padding: '6px 10px',
                fontSize: 13,
                border: '1px solid var(--border-strong)',
                borderRadius: 4,
              }}
            />
            <button
              type="submit"
              disabled={createMut.isPending || !newEmail.trim()}
              style={{
                padding: '6px 14px',
                fontSize: 13,
                background: 'var(--accent-primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: createMut.isPending ? 'wait' : 'pointer',
                fontWeight: 600,
              }}
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => {
                setCreateOpen(false);
                setNewEmail('');
                setNewDisplayName('');
              }}
              style={{
                padding: '6px 14px',
                fontSize: 13,
                background: 'transparent',
                color: 'var(--text-muted)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </form>
        )}

        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          aria-label="Search users"
          style={{
            width: '100%',
            padding: '6px 10px',
            fontSize: 13,
            border: '1px solid var(--border-strong)',
            borderRadius: 4,
            marginBottom: 12,
          }}
        />

        {usersQ.isLoading ? (
          <div style={{ color: 'var(--text-muted)' }}>Loading users…</div>
        ) : users.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic', padding: '12px 0' }}>
            {search ? 'No users match.' : showInactive ? 'No users.' : 'No active users. Toggle "Show disabled" to see disabled accounts.'}
          </div>
        ) : (
          // Same fixed-layout pattern as MembersPanel + GlobalRolesPanel —
          // explicit colgroup widths, nowrap + ellipsis on every cell,
          // title attr for hover-to-see-full.
          <table className="grid" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 180 }} />
              <col style={{ width: 280 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 260 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Display name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Created</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <Fragment key={u.id}>
                <tr style={{ opacity: u.isActive ? 1 : 0.55 }}>
                  <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.displayName}>
                    {u.displayName}
                  </td>
                  <td
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 12,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={u.email}
                  >
                    {u.email}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {u.isActive ? (
                      <span style={{ color: 'var(--success-primary)', fontWeight: 600 }}>active</span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>disabled</span>
                    )}
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {/* CTSK-238 smoke fix (2026-07-30): wrap the two row
                        buttons in an inline-flex container so they lay out
                        horizontally regardless of column-width squeeze.
                        Prior version relied on `marginRight: 6` between two
                        block-ish buttons; when the Created column
                        auto-expanded (no explicit width in colgroup) the
                        Actions column got squeezed and the two buttons
                        stacked vertically. Also gave Created an explicit
                        width above so this can't re-happen. */}
                    <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                      <button
                        type="button"
                        title="Show this user's role in every org they belong to"
                        onClick={() => setExpandedUserId(expandedUserId === u.id ? null : u.id)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '4px 10px',
                          fontSize: 12,
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 4,
                          background: 'transparent',
                          color: 'var(--text-secondary)',
                          cursor: 'pointer',
                        }}
                      >
                        {expandedUserId === u.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        <Building2 size={12} /> Org roles
                      </button>
                      <button
                        type="button"
                        title="Reset password (generates a new one-time password; old password stops working immediately)"
                        onClick={() => {
                          if (confirm(`Reset password for ${u.email}? Their current password will stop working immediately.`)) {
                            resetPasswordMut.mutate({ id: u.id });
                          }
                        }}
                        disabled={resetPasswordMut.isPending}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '4px 10px',
                          fontSize: 12,
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 4,
                          background: 'transparent',
                          color: 'var(--text-secondary)',
                          cursor: 'pointer',
                        }}
                      >
                        <KeyRound size={12} /> Reset password
                      </button>
                      <button
                        type="button"
                        title={u.isActive ? 'Click to disable (revokes login)' : 'Click to enable'}
                        onClick={() => {
                          const verb = u.isActive ? 'Disable' : 'Enable';
                          if (confirm(`${verb} user ${u.email}?`)) {
                            toggleActiveMut.mutate({ id: u.id, isActive: !u.isActive });
                          }
                        }}
                        disabled={toggleActiveMut.isPending}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '4px 10px',
                          fontSize: 12,
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 4,
                          background: 'transparent',
                          color: u.isActive ? 'var(--danger-primary, #B3261E)' : 'var(--success-primary)',
                          cursor: 'pointer',
                        }}
                      >
                        {u.isActive ? <><UserX size={12} /> Disable</> : <><UserCheck size={12} /> Enable</>}
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedUserId === u.id && (
                  <tr>
                    <td colSpan={5} style={{ background: 'var(--surface-sunken)', padding: '10px 16px' }}>
                      {membershipsQ.isLoading ? (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading org roles…</span>
                      ) : membershipsQ.isError ? (
                        <span style={{ fontSize: 12, color: 'var(--danger-primary, #B3261E)' }}>
                          Failed to load org roles.
                        </span>
                      ) : (membershipsQ.data?.data.length ?? 0) === 0 ? (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Not a member of any org.</span>
                      ) : (
                        <table style={{ width: '100%', fontSize: 12 }}>
                          <thead>
                            <tr style={{ color: 'var(--text-muted)' }}>
                              <th style={{ textAlign: 'left', fontWeight: 500, paddingRight: 12 }}>Organization</th>
                              <th style={{ textAlign: 'left', fontWeight: 500, paddingRight: 12 }}>Role</th>
                              <th style={{ textAlign: 'left', fontWeight: 500, paddingRight: 12 }}>Owner</th>
                              <th style={{ textAlign: 'left', fontWeight: 500 }}>Joined</th>
                            </tr>
                          </thead>
                          <tbody>
                            {membershipsQ.data!.data.map((m) => (
                              <tr key={m.orgId}>
                                <td style={{ paddingRight: 12 }}>{m.orgName}</td>
                                <td style={{ paddingRight: 12 }}>
                                  {m.isOwner ? (
                                    <span style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>{m.roleName}</span>
                                  ) : (
                                    <span style={{ color: 'var(--text-secondary)' }}>{m.roleName}</span>
                                  )}
                                </td>
                                <td style={{ paddingRight: 12, color: 'var(--text-muted)' }}>{m.isOwner ? 'yes' : 'no'}</td>
                                <td style={{ color: 'var(--text-muted)' }}>{new Date(m.joinedAt).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section style={{ background: 'var(--surface-sunken)', padding: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', fontSize: 12, color: 'var(--text-muted)' }}>
        <strong>Capability notes</strong>
        <ul style={{ margin: '6px 0 0 16px', padding: 0, lineHeight: 1.6 }}>
          <li><code>users:view</code> — list / search / read users (every built-in role holds it).</li>
          <li><code>users:create</code> — mint new user accounts (emissary / steward / envoy + owner).</li>
          <li><code>users:manage</code> — full power: disable / enable / rename / email change (owner only by default; can be granted via custom role).</li>
        </ul>
        <p style={{ margin: '8px 0 0 0' }}>
          Password reset (CTSK-238) is its own endpoint (<code>POST /users/:id/reset-password</code>,
          gated on <code>users:manage</code>) so the audit + temp-password flow stays distinct. The new
          password is shown exactly once in the banner above — hand it to the user out-of-band.
        </p>
      </section>
    </div>
  );
}

// Silence unused-import lint for apiFetch (kept for future actions).
void apiFetch;
